import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { nok, num } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SectionShell } from "./SectionShell";
import { qtyLabel, unitPriceLabel } from "./proposalUtils";

const Line = ({ l, testId }) => (
  <li className="grid grid-cols-[1fr_auto] items-baseline gap-x-6 gap-y-1 py-3 sm:grid-cols-[1fr_auto_auto]" data-testid={testId}>
    <div className="min-w-0">
      <div className="text-[14.5px] leading-6">{l.description}</div>
      <div className="text-[12.5px] text-muted-foreground sm:hidden">{qtyLabel(l)}{l.sell_unit != null ? ` · ${unitPriceLabel(l)}` : ""}{l.discount_pct ? ` · −${num(l.discount_pct, 1)} %` : ""}</div>
    </div>
    <div className="hidden text-right font-mono text-[12.5px] tabular text-muted-foreground sm:block">
      {qtyLabel(l)}{l.sell_unit != null ? <span className="ml-2 text-muted-foreground/70">à {unitPriceLabel(l)}</span> : null}
      {l.discount_pct ? <span className="ml-2 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">−{num(l.discount_pct, 1)} %</span> : null}
    </div>
    <div className="text-right font-mono text-[14.5px] tabular">{nok(l.sell_total)}</div>
  </li>
);

const Group = ({ g, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const id = `pricing-group-${g.key}`;
  return (
    <div className="border-b border-border/70" data-testid={`proposal-pricing-group-${g.key}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={id}
        className="group flex w-full items-start justify-between gap-6 py-5 text-left transition-colors duration-150 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 rounded-md"
        data-testid={`proposal-pricing-group-toggle-${g.key}`}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[17px] font-semibold tracking-tight">
            {g.label}
            <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", open && "rotate-180")} />
          </div>
          {g.intro && <div className="mt-0.5 text-[13px] text-muted-foreground">{g.intro} <span className="text-muted-foreground/70">{g.lines.length} {g.lines.length === 1 ? "post" : "poster"}</span></div>}
        </div>
        <div className="shrink-0 font-mono text-[16px] tabular font-medium" data-testid={`proposal-pricing-subtotal-${g.key}`}>{nok(g.subtotal)}</div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="lines" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
            <ul id={id} className="-mt-1 mb-3 divide-y divide-border/50 rounded-xl bg-muted/40 px-4 sm:px-5" data-testid={`proposal-pricing-lines-${g.key}`}>
              {g.lines.map((l, i) => <Line key={l.id || i} l={l} testId={`proposal-line-${g.key}-${i}`} />)}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SHADES = ["bg-primary", "bg-[hsl(206,100%,30%)]", "bg-[hsl(var(--navy))]", "bg-slate-400", "bg-slate-300", "bg-slate-200"];

/** Stacked bar: how the total splits across the groups (top 5 + rest). */
const Distribution = ({ pr }) => {
  const total = pr.groups.reduce((s, g) => s + (g.subtotal || 0), 0);
  if (!total) return null;
  const sorted = [...pr.groups].sort((a, b) => b.subtotal - a.subtotal);
  const top = sorted.slice(0, 5);
  const rest = sorted.slice(5).reduce((s, g) => s + g.subtotal, 0);
  const segs = [...top.map((g, i) => ({ key: g.key, label: g.label, v: g.subtotal, cls: SHADES[i] })), ...(rest > 0 ? [{ key: "rest", label: "Øvrig", v: rest, cls: SHADES[5] }] : [])];
  return (
    <div className="rounded-2xl bg-muted/50 p-5" data-testid="proposal-pricing-distribution">
      <div className="text-[12.5px] text-muted-foreground">Slik fordeler summen seg</div>
      <div className="mt-3 flex h-2.5 w-full overflow-hidden rounded-full bg-border/60">
        {segs.map((sg, i) => (
          <motion.div key={sg.key} className={cn("h-full", sg.cls)} initial={{ width: 0 }} whileInView={{ width: `${(sg.v / total) * 100}%` }} viewport={{ once: true }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 + i * 0.05 }} title={`${sg.label}: ${nok(sg.v)}`} />
        ))}
      </div>
      <ul className="mt-3 space-y-1.5 text-[12.5px]">
        {segs.map((sg) => (
          <li key={sg.key} className="flex items-center justify-between gap-3">
            <span className="inline-flex min-w-0 items-center gap-2 text-muted-foreground"><span className={cn("h-2 w-2 shrink-0 rounded-full", sg.cls)} /> <span className="truncate">{sg.label}</span></span>
            <span className="font-mono tabular">{Math.round((sg.v / total) * 100)} %</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** 03 · Pris: grouped, transparent specification; totals with VAT; monthly vs establishment; optional add-ons. */
export const ProposalPricing = ({ data }) => {
  const pr = data.pricing;
  const aside = (
    <div className="space-y-4">
    <dl className="space-y-4 rounded-2xl bg-muted/50 p-5 text-[14px]" data-testid="proposal-pricing-summary">
      <div className="flex items-baseline justify-between gap-4"><dt className="text-muted-foreground">Sum eks. mva</dt><dd className="font-mono tabular font-medium">{nok(pr.revenue)}</dd></div>
      {pr.monthly_total ? <div className="flex items-baseline justify-between gap-4"><dt className="text-muted-foreground">Per måned (leie)</dt><dd className="font-mono tabular">{nok(pr.monthly_total)}</dd></div> : null}
      {pr.establishment_total ? <div className="flex items-baseline justify-between gap-4"><dt className="text-muted-foreground">Etablering (engangs)</dt><dd className="font-mono tabular">{nok(pr.establishment_total)}</dd></div> : null}
      {pr.global_discount_pct ? <div className="flex items-baseline justify-between gap-4"><dt className="text-muted-foreground">Avtalt rabatt</dt><dd className="font-mono tabular">−{num(pr.global_discount_pct, 1)} %</dd></div> : null}
      <p className="pt-1 text-[12.5px] leading-5 text-muted-foreground">Alle priser i norske kroner eks. mva. Leie faktureres månedlig; etablering ved levering.</p>
    </dl>
    <Distribution pr={pr} />
    </div>
  );

  return (
    <SectionShell id="pris" no="03" title="Prisspesifikasjon" lede="Full transparens – hver post, gruppert slik leveransen er bygget opp." aside={aside}>
      <div data-testid="proposal-pricing">
        {pr.groups.map((g, i) => <Group key={g.key} g={g} defaultOpen={i < 2} />)}

        <dl className="mt-8 ml-auto max-w-md space-y-3 text-[15px]" data-testid="proposal-totals">
          {pr.global_discount_pct && pr.revenue_gross ? <div className="flex items-baseline justify-between gap-6"><dt className="text-muted-foreground">Sum før rabatt</dt><dd className="font-mono tabular text-muted-foreground line-through">{nok(pr.revenue_gross)}</dd></div> : null}
          <div className="flex items-baseline justify-between gap-6"><dt className="text-muted-foreground">Sum eks. mva</dt><dd className="font-mono tabular" data-testid="proposal-total-ex-vat">{nok(pr.revenue)}</dd></div>
          <div className="flex items-baseline justify-between gap-6"><dt className="text-muted-foreground">Mva {num(pr.vat_pct)} %</dt><dd className="font-mono tabular" data-testid="proposal-total-vat">{nok(pr.vat)}</dd></div>
          <div className="flex items-baseline justify-between gap-6 border-t border-foreground/80 pt-4">
            <dt className="text-[16px] font-semibold">Totalt inkl. mva</dt>
            <dd className="font-mono text-[24px] font-semibold tabular tracking-tight sm:text-[28px]" data-testid="proposal-total-incl-vat">{nok(pr.total_incl_vat)}</dd>
          </div>
        </dl>

        {pr.options?.length > 0 && (
          <div className="mt-14" data-testid="proposal-options">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-[17px] font-semibold tracking-tight">Tilvalg</h3>
              <span className="text-[12.5px] text-muted-foreground">Ikke inkludert i totalsummen – bestilles ved behov</span>
            </div>
            <ul className="mt-3 divide-y divide-border/60 rounded-xl border border-dashed border-border px-4 sm:px-5">
              {pr.options.map((l, i) => <Line key={l.id || i} l={l} testId={`proposal-option-${i}`} />)}
            </ul>
          </div>
        )}
      </div>
    </SectionShell>
  );
};
